
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { venuesDetailData } from '../../mocks/venuesDetail';
import VenuesDetailHeader from './components/VenuesDetailHeader';
import VenuesDetailFooter from './components/VenuesDetailFooter';
import VenueCard from './components/VenueCard';

const areas = ['すべて', ...Array.from(new Set(venuesDetailData.map(v => v.area)))];

export default function VenuesDetailPage() {
  const [selectedArea, setSelectedArea] = useState('すべて');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredVenues = useMemo(() => {
    return venuesDetailData.filter(venue => {
      const matchArea = selectedArea === 'すべて' || venue.area === selectedArea;
      const matchSearch = searchQuery === '' ||
        venue.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        venue.area.includes(searchQuery) ||
        venue.genre.includes(searchQuery);
      return matchArea && matchSearch;
    });
  }, [selectedArea, searchQuery]);

  return (
    <div className="min-h-screen bg-white">
      <VenuesDetailHeader />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <Link to="/" className="hover:text-gray-900 transition-colors cursor-pointer">トップページ</Link>
              <i className="ri-arrow-right-s-line text-gray-400"></i>
              <span className="text-gray-900 font-medium">参加会場一覧（詳細）</span>
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              <a href="#venues-detail-list" className="hover:opacity-80 transition-opacity">参加会場一覧</a>
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
              東京支部に加盟する各ライブハウスの詳細情報です。公式サイトやSNS、予約ページへのリンクを掲載しています。各会場の特色やジャンル、連絡先をご確認のうえ、お気軽にお問い合わせください。
            </p>
          </div>
        </section>

        {/* Filter & Search */}
        <section className="py-8 border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-wrap">
                {areas.map(area => (
                  <button
                    key={area}
                    onClick={() => setSelectedArea(area)}
                    className={`px-4 py-2 text-sm rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                      selectedArea === area
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {area}
                  </button>
                ))}
              </div>
              <div className="relative w-full md:w-72">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="会場名・エリア・ジャンルで検索"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Venues List */}
        <section id="venues-detail-list" className="py-16">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-8">
              <p className="text-sm text-gray-500">
                <strong className="text-gray-900">{filteredVenues.length}</strong> 件の会場が見つかりました
              </p>
            </div>

            {filteredVenues.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredVenues.map((venue, index) => (
                  <VenueCard key={index} venue={venue} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                  <i className="ri-search-line text-2xl text-gray-400"></i>
                </div>
                <p className="text-gray-500 mb-2">該当する会場が見つかりませんでした</p>
                <button
                  onClick={() => { setSelectedArea('すべて'); setSearchQuery(''); }}
                  className="text-sm text-gray-900 underline hover:no-underline cursor-pointer whitespace-nowrap"
                >
                  フィルターをリセット
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">会場の参加をご検討の方へ</h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              東京支部への加盟にご興味のあるライブハウス運営者の方は、お気軽にお問い合わせください。まずは試験的な連携から始めることも可能です。
            </p>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-md text-sm font-semibold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line"></i>
              お問い合わせはこちら
            </a>
          </div>
        </section>
      </main>

      <VenuesDetailFooter />
    </div>
  );
}
